import { Box } from '@material-ui/core';
import { setCredentials, setEnvironment, setGlobalMiddlewares } from '@nemuru-eng/js-api-client';
import { useAuth } from '@nemuru-eng/providers';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useInitApplication } from '@/contexts/UserContext/hooks/useInitApplication';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import { useLayoutConstructor } from '@/hooks/useLayoutConstructor';
import { clearSessionLocalStorage } from '@/utils/clearSessionLocalStorage';

import { ENV_COOKIE_LOGIN_NAME, ENV_ENV } from '../../../../config/envVars';
import { useConfigContext } from '../../../../contexts/ConfigContext/ConfigContext';
import { publicPaths } from '../../../../Routes';
import { AbsoluteLinearProgress } from '../AbsoluteLinearProgress';

export function AuthenticationLayer(props) {
  const { appStarted } = useUserContext();
  const {
    state: { currentLayout },
    actions: configActions,
  } = useConfigContext();

  const location = useLocation();
  const { credentials, AuthMiddleware } = useAuth();
  const { initApplication } = useInitApplication();

  useEffect(() => {
    initCore();
    showDevOptionsIfDebugFlagTrue();

    // Clears the localStorage values scoped to the authenticated session (keys prefixed by clearSessionLocalStorage)
    // when the user logs out. AuthProvider only renders its children while the user has access, so this layer
    // unmounts on every logout: the account page, the develop panel, an unsupported role (useInitApplication) or the
    // provider's auto logout on inactivity, which the app has no hook into. Every route, the 404 one included, is a
    // child of App, so navigating never unmounts it, and React skips cleanups on a page reload or tab close.
    // The change password screen also unmounts the app but keeps the session going. Logging out removes the
    // stored credentials before unmounting the app, and changing the password does not, so they tell both apart.
    // Known gap: a session that expires while the app is closed is logged out before the app mounts, so nothing
    // clears and the same user finds the previous values on the next login.
    // TODO: move this to an onLogout callback on AuthProvider once @nemuru-eng/providers exposes one. Every logout
    // goes through the provider's logout, the expired session on start up included, so it would close the gap.
    return () => {
      if (!hasStoredCredentials()) {
        clearSessionLocalStorage();
      }
    };
  }, []);

  useLayoutConstructor();

  if (!credentials) return <></>;

  const initCore = () => {
    setEnvironment(ENV_ENV);
    setGlobalMiddlewares([AuthMiddleware]);
    setCredentials(credentials);
    initApplication();
  };

  const showDevOptionsIfDebugFlagTrue = () => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has('debug') && queryParams.get('debug') === 'true') {
      configActions.setShowDevelopOptionsPanel(true);
    }
  };

  const isCurrentPathPublic = () => {
    // TODO role checking?
    return publicPaths.indexOf(location.pathname) !== -1;
  };

  const appReady = appStarted && currentLayout;

  const showContent = appReady || isCurrentPathPublic();

  return (
    <>
      {showContent ? (
        <>{props.children}</>
      ) : (
        <Box
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <>
            <AbsoluteLinearProgress show={true} style={{ position: 'fixed' }} />
          </>
        </Box>
      )}
    </>
  );
}

// Same storage key AuthProvider keeps the credentials in (storageKey in index.tsx)
const hasStoredCredentials = () => {
  try {
    return !!localStorage.getItem(ENV_COOKIE_LOGIN_NAME);
  } catch {
    // Restricted storage: nothing session scoped could be written either, so there is nothing to clear
    return true;
  }
};
